import type { Subscription } from "@innoclouds/db";
import { liveQuery } from "@innoclouds/db";
import { tryOnScopeDispose } from "@vueuse/core";
import type { MaybeRefOrGetter, WatchOptions } from "vue";
import { computed, ref, watch } from "vue";

type Value<T, I> = I extends undefined ? T | undefined : T | I;
type UseLiveQueryWithDepsOptions<I, Immediate> = {
	onError?: (error: any) => void;
	initialValue?: I;
} & WatchOptions<Immediate>;

export const useLiveQuery = <T, I = undefined, Immediate extends Readonly<boolean> = true>(
	querier: (...data: any) => T | Promise<T>,
	options: UseLiveQueryWithDepsOptions<I, Immediate> = {},
	deps: MaybeRefOrGetter[] = [],
) => {
	const { onError, initialValue, ...rest } = options;

	const data = ref<T | I | undefined>(initialValue);
	let subscription: Subscription | undefined;

	const start = (...params: any) => {
		subscription?.unsubscribe();
		const observable = liveQuery(() => querier(...params));
		subscription = observable.subscribe({
			next: (result) => {
				data.value = result;
			},
			error: (error) => {
				onError?.(error);
			},
		});
	};

	const cleanup = () => {
		subscription?.unsubscribe();
		subscription = undefined;
	};

	watch(deps, (newValues) => start(...newValues), { immediate: true, ...rest });
	if (!deps?.length && options?.immediate !== false) start();

	tryOnScopeDispose(() => {
		cleanup();
	});

	return {
		data: computed<Value<T, I>>(() => data.value),
		stop: cleanup,
		start: start,
	};
};
